<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import { useJudgmentOverview, useInitMasters } from '@/api/queries'
import StatusIcon from './StatusIcon.vue'
import { resolveStatusStyle } from '@/utils/judgmentStatus'

const selection = useSelectionStore()
const expanded = ref(true)

// 「絞り込む」ボタン押下後、一覧データの取得中はスピナーを表示する
// (OfferTable.vue側と同じクエリキーで呼び出すため、react-queryのキャッシュを
//  共有するだけで、追加のAPI呼び出しは発生しない)
const overviewParams = computed(() => selection.effectiveParams)
const { isFetching: isOverviewFetching } = useJudgmentOverview(overviewParams)

// 状態タブは marketplace.mjudgmentstatus から動的に生成する。
// VB.NET版も ComboBoxRunJudgeStatus をマスタ全件から作っていたため、それに合わせる
// (固定リストにしていると、マスタが増えたときに選べない状態が生まれる)。
// 絞り込みは (JudgmentStatusCode, JudgmentSubStatusCode) の組で行う。
const { data: masters } = useInitMasters()

interface StatusTab {
  key: string
  subKey?: string
  label: string
}

const allStatusTabs = computed<StatusTab[]>(() =>
  (masters.value?.judgmentStatusMaster ?? []).map((row) => ({
    key: row.JudgmentStatusCode,
    subKey: row.JudgmentSubStatusCode,
    label: row.JudgmentStatusName,
  })),
)

/**
 * 常時チップとして出す状態。実データの件数が多いものだけに絞っている。
 *   (1,1)準備 7,850 / (1,2)審査 1,423 / (2,0)OK 325,069 / (8,0)出品NG 2,746 / (9,0)審査NG 335,932
 * これ以外(審査中(不明)・保留・反映待ち4種)は実データがほぼ無いので「その他」へ送る。
 * マスタが増えても自動的に「その他」に入るので、選べなくなることはない。
 */
const PRIMARY_STATUS = new Set(['11', '12', '20', '80', '90'])
const tabKey = (t: StatusTab) => `${t.key}${t.subKey ?? ''}`

const primaryTabs = computed<StatusTab[]>(() => [
  { key: 'all', label: 'すべて' },
  ...allStatusTabs.value.filter((t) => PRIMARY_STATUS.has(tabKey(t))),
])
const otherTabs = computed<StatusTab[]>(() =>
  allStatusTabs.value.filter((t) => !PRIMARY_STATUS.has(tabKey(t))),
)

// 「その他」から選んだ状態は、選択中であることが分かるようにチップとしても表示する
const selectedOtherTab = computed(() => otherTabs.value.find((t) => isActiveTab(t)) ?? null)

// 「その他」メニューの開閉。
// mouseleave で閉じると、ボタンからメニューへカーソルを移す途中で閉じてしまい
// 項目を選べない(通り道次第で挙動が変わる)。クリックで開き、
// 外側クリック / Escape でのみ閉じる方式にする。
const otherMenuOpen = ref(false)
const otherRef = ref<HTMLElement | null>(null)

function onDocPointerDown(e: PointerEvent) {
  if (!otherMenuOpen.value) return
  if (otherRef.value?.contains(e.target as Node)) return
  otherMenuOpen.value = false
}
function onDocKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') otherMenuOpen.value = false
}
onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown)
  document.addEventListener('keydown', onDocKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown)
  document.removeEventListener('keydown', onDocKeydown)
})

function selectTab(tab: StatusTab) {
  selection.statusFilter = tab.key
  selection.filters = { ...selection.filters, judgmentSubStatusCode: tab.subKey }
  otherMenuOpen.value = false
}

/**
 * ステータスの色分け。キーは (JudgmentStatusCode + SubStatusCode)。
 * 一覧のバッジや本文マーキングと同じ意味の色を使い、
 * 「この色＝この状態」を画面全体で一貫させる。
 */
function statusTone(tab: StatusTab): string {
  if (tab.key === 'all') return 'all'
  return resolveStatusStyle(tab.key, tab.subKey).tone
}
function statusIcon(tab: StatusTab): string {
  if (tab.key === 'all') return 'list'
  return resolveStatusStyle(tab.key, tab.subKey).icon
}

function isActiveTab(tab: StatusTab) {
  if (tab.key === 'all') return selection.statusFilter === 'all'
  return (
    selection.statusFilter === tab.key &&
    selection.filters.judgmentSubStatusCode === tab.subKey
  )
}

const localName = ref('')
const localBrand = ref('')
const localSellerId = ref('')
const localParentAsin = ref('')
const localChildAsin = ref('')
/**
 * 「一時停止・削除を含む」「販売無効化中を含む」は、
 * 「プレミアムのみ」と同様にチェックした時点で即座に絞り込む。
 * (ローカルrefに溜めて「絞り込む」を押すまで反映しない作りだと、
 *  同じ並びのチェックボックスなのに挙動が食い違い、
 *  さらに絞り込みタグを消してもチェックが外れない不整合が起きる)
 *
 * false のときはキー自体を消す。値が残っているとタグの表示判定
 * (filterTags)で「条件あり」と誤認されるため。
 */
const includeDeleted = computed({
  get: () => selection.filters.includeDeleted === true,
  set: (v) => {
    const next = { ...selection.filters }
    if (v) next.includeDeleted = true
    else delete next.includeDeleted
    selection.filters = next
  },
})
const includeDisabled = computed({
  get: () => selection.filters.includeDisabled === true,
  set: (v) => {
    const next = { ...selection.filters }
    if (v) next.includeDisabled = true
    else delete next.includeDisabled
    selection.filters = next
  },
})

function applyFilter() {
  selection.filters = {
    ...selection.filters,
    productName: localName.value || undefined,
    brand: localBrand.value || undefined,
    sellerId: localSellerId.value || undefined,
    parentAsin: localParentAsin.value || undefined,
    childAsin: localChildAsin.value || undefined,
  }
}

// ---- 再審査 / 新規 ----
// 【ステータスタブに混ぜない理由】
// タブは「審査の結果」の軸、こちらは「審査履歴の有無」の軸で、
// 掛け合わせて使いたい(例: 審査中 かつ 新規)。同じタブに入れると
// 排他になってしまい、その組み合わせができなくなる。
const JUDGED_OPTIONS = [
  { key: 'rejudge', label: '再審査', hint: '過去に審査したことがある商品' },
  { key: 'new', label: '新規', hint: 'まだ一度も審査していない商品' },
] as const

function toggleJudged(key: 'rejudge' | 'new') {
  const next = { ...selection.filters }
  if (next.judgedFilter === key) delete next.judgedFilter
  else next.judgedFilter = key
  selection.filters = next
}

function clearFilter() {
  localName.value = ''
  localBrand.value = ''
  localSellerId.value = ''
  localParentAsin.value = ''
  localChildAsin.value = ''
  // チェックボックスはストア直結なので、filters を空にすれば自動的に外れる。
  // 「プレミアムのみ」も同じ並びのチェックボックスなので一緒に解除する。
  selection.clearAllFilters()
}
</script>

<template>
  <div class="filter-block">
    <div class="status-tabs-row">
      <div class="status-tabs">
        <button
          v-for="tab in primaryTabs"
          :key="tab.label"
          class="status-tab"
          :class="{ active: isActiveTab(tab) }"
          :data-s="statusTone(tab)"
          @click="selectTab(tab)"
        >
          <StatusIcon :name="statusIcon(tab)" />{{ tab.label }}
        </button>

        <!-- 「その他」から選択中の状態は、選ばれていることが分かるようチップにも出す -->
        <button
          v-if="selectedOtherTab"
          class="status-tab active"
          :data-s="statusTone(selectedOtherTab)"
          @click="selectTab(selectedOtherTab)"
        >
          <StatusIcon :name="statusIcon(selectedOtherTab)" />{{ selectedOtherTab.label }}
        </button>

        <!-- 使用頻度の低い状態はドリルダウンにまとめる -->
        <div v-if="otherTabs.length" ref="otherRef" class="status-other">
          <button
            class="status-tab status-tab--other"
            :class="{ open: otherMenuOpen }"
            @click="otherMenuOpen = !otherMenuOpen"
          >
            その他
            <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <div v-if="otherMenuOpen" class="status-other-menu">
            <button
              v-for="tab in otherTabs"
              :key="tab.label"
              class="status-other-item"
              :class="{ active: isActiveTab(tab) }"
              @click="selectTab(tab)"
            >{{ tab.label }}</button>
          </div>
        </div>
      </div>
      <button class="filter-toggle" @click="expanded = !expanded">
        フィルタ
        <svg class="icon icon-xs" viewBox="0 0 24 24" :style="{ transform: expanded ? '' : 'rotate(-90deg)' }">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
    </div>

    <div class="filter-fields" v-show="expanded">
      <div class="field-row">
        <div class="field">
          <label>セラーID <span style="font-weight:400;color:var(--ink-faint)">(完全一致)</span></label>
          <input type="text" v-model="localSellerId" placeholder="例: A2X9K...">
        </div>
        <div class="field">
          <label>ブランド</label>
          <input type="text" v-model="localBrand" placeholder="ブランド名">
        </div>
      </div>
      <div class="field-row">
        <div class="field">
          <label>親ASIN</label>
          <input type="text" v-model="localParentAsin" placeholder="B0XXXXXXX">
        </div>
        <div class="field">
          <label>子ASIN</label>
          <input type="text" v-model="localChildAsin" placeholder="B0XXXXXXX">
        </div>
      </div>
      <div class="field-row">
        <div class="field" style="grid-column: 1 / -1;">
          <label>商品名 <span style="font-weight:400;color:var(--ink-faint)">(部分一致)</span></label>
          <input type="text" v-model="localName" placeholder="キーワード">
        </div>
      </div>
      <!-- 再審査/新規: 審査状態(タブ)とは別の軸なので、掛け合わせて使える -->
      <div class="filter-sub">
        <span class="filter-sub-label">審査履歴</span>
        <button
          v-for="o in JUDGED_OPTIONS"
          :key="o.key"
          class="chip-toggle"
          :class="{ active: selection.filters.judgedFilter === o.key }"
          :title="o.hint"
          @click="toggleJudged(o.key)"
        >{{ o.label }}</button>
      </div>

      <div class="check-row">
        <label class="chk"><input type="checkbox" v-model="includeDeleted"> 一時停止・削除を含む</label>
        <label class="chk"><input type="checkbox" v-model="includeDisabled"> 販売無効化中を含む</label>
        <label class="chk"><input type="checkbox" v-model="selection.premiumOnly"> プレミアムのみ</label>
      </div>
      <div class="filter-actions">
        <button class="btn btn-primary" style="flex:1" :disabled="isOverviewFetching" @click="applyFilter">
          <span v-if="isOverviewFetching" class="spinner spinner-sm"></span>
          <svg v-else class="icon icon-sm" viewBox="0 0 24 24"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          絞り込む
        </button>
        <button class="btn btn-ghost" @click="clearFilter">
          <svg class="icon icon-sm" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          クリア
        </button>
      </div>
    </div>
  </div>
</template>
